import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { EmeraldButtonModule } from '@emerald-angular/button';
import { EmeraldDropdownModule } from '@emerald-angular/dropdown';
import { EmeraldModalModule } from '@emerald-angular/modal';
import { ContractorsVendorView } from '@pages/vendor-rep/contractors-list/contractors-list.config';
import { CommonModule } from '@angular/common';
import { interval, takeWhile } from 'rxjs';
import { dropdownOptions } from '@interfaces/vendor.model';
import { GlobalErrorService } from '@services/global-error.service';
import { VendorService } from '@services/vendor.service';
import { filterContractorsList } from './contractor-list-filter.helper';

@Component({
  selector: 'app-contractor-list-filter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    EmeraldModalModule,
    EmeraldDropdownModule,
    EmeraldButtonModule,
  ],
  templateUrl: './contractor-list-filter.component.html',
  styleUrl: './contractor-list-filter.component.scss',
})
export class ContractorListFilterComponent {
  @Input() ContractorDataByFilter: ContractorsVendorView[] = [];
  @Input() vendorId!: string;
  @Output() filterApplied = new EventEmitter<any>();
  @Output() closeFilter = new EventEmitter<void>();
  @Output() filterReset = new EventEmitter<void>();

  filterForm: FormGroup = new FormGroup({});
  statusOptions: dropdownOptions[] = [];
  locationOptions: dropdownOptions[] = [];
  reportingManagerOptions: dropdownOptions[] = [];
  opened: boolean = false;
  openForm: boolean = true;
  displayData: any[] = [];
  rowData: any[] = [];
  monthOptions: dropdownOptions[] = [];
  selectedMonth!: number;
  selectedYear!: number;
  private isDataLoaded: boolean = false;
  selectedOption: string = '';

  constructor(
    public fb: FormBuilder,
    public globalError: GlobalErrorService,
    private vendorService: VendorService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.generateMonthOptions();
    this.loadFormData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['ContractorDataByFilter'] &&
      changes['ContractorDataByFilter'].currentValue
    ) {
      this.loadFormData();
    }
  }

  initializeForm(): void {
    this.filterForm = this.fb.group({
      status: [null, Validators.required],
      location: [null, Validators.required],
      reportingManager: [null, Validators.required],
      month: [null, Validators.required],
    });
  }

  generateMonthOptions(): void {
    const today = new Date();
    this.monthOptions = Array.from({ length: 12 }, (_, i) => {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      return {
        label: `${monthName}, ${year}`,
        value: `${date.getMonth() + 1}-${year}`,
      };
    });
    if (!this.selectedOption && this.monthOptions.length) {
      this.selectedOption = this.monthOptions[0].value;
    }
  }

  filterDataByMonthAndYear(): void {
    this.vendorService.fetchContractorsByVendorId(
      this.vendorId,
      this.selectedMonth,
      this.selectedYear,
    );

    const signalData = this.vendorService.contractors();
    if (signalData && signalData?.data) {
      this.ContractorDataByFilter = signalData?.data.map(
        (contractor: ContractorsVendorView) => ({
          contractorName: contractor.contractorName || '',
          status: contractor.status || '',
          location: contractor.location || '',
          reportingManager: contractor.reportingManager || '',
          sowExpiryDate: contractor.sowExpiryDate || '',
          totalExperience: contractor.totalExperience || '',
          dateOfJoining: contractor.dateOfJoining || '',
        }),
      );
      this.displayData = this.ContractorDataByFilter;
      this.cdr.detectChanges();
    }
  }

  loadFormData() {
    if (this.ContractorDataByFilter && this.ContractorDataByFilter.length > 0) {
      this.statusOptions = this.getUniqueValues('status');
      this.locationOptions = this.getUniqueValues('location');
      this.reportingManagerOptions = this.getUniqueValues('reportingManager');
      this.isDataLoaded = true;
    }
  }

  private getUniqueValues(key: keyof ContractorsVendorView): dropdownOptions[] {
    if (
      !this.ContractorDataByFilter ||
      this.ContractorDataByFilter.length === 0
    ) {
      return [];
    }

    const values = this.ContractorDataByFilter.map((item) => item[key]).filter(
      (value) => value !== '' && value !== null && value !== undefined,
    );

    const uniqueValues = [...new Set(values)];

    return uniqueValues.map((value) => ({
      label: String(value),
      value: String(value),
    }));
  }

  apply(): void {
    const getFilterValue = (field: string) => {
      const value = this.filterForm.get(field)?.value;
      return value?.value || value || null;
    };

    const selectedStatus = getFilterValue('status');
    const selectedLocation = getFilterValue('location');
    const selectedReportingManager = getFilterValue('reportingManager');
    const selectedMonth = getFilterValue('month');

    if (selectedMonth) {
      const combinedFilters = {
        month: selectedMonth,
        year: this.selectedYear,
        status: selectedStatus,
        location: selectedLocation,
        reportingManager: selectedReportingManager,
      };

      this.filterApplied.emit(combinedFilters);
      this.closeFilter.emit();
      return;
    }

    const filterCriteria = {
      status: selectedStatus,
      location: selectedLocation,
      reportingManager: selectedReportingManager,
    };
    this.displayData = filterContractorsList(
      this.ContractorDataByFilter,
      filterCriteria,
    );
    this.filterApplied.emit(this.displayData);
    this.closeFilter.emit();
  }

  onMonthSelect(event: any): void {
    this.selectedOption = event;
    const t = this.setMonthAndYear(this.selectedOption);
    this.selectedYear = Number(t.split('-')[1]);
    this.selectedMonth = Number(t.split('-')[0]);

    this.filterForm.patchValue({ month: event });
    this.apply();
  }

  setMonthAndYear(value: string): string {
    return value;
  }

  reset() {
    this.filterForm.patchValue({
      status: null,
      location: null,
      reportingManager: null,
      month: null,
    });

    this.opened = true;
    this.openForm = false;
    setTimeout(() => {
      this.openForm = true;
    });

    this.filterApplied.emit(this.ContractorDataByFilter);
    this.filterReset.emit();
  }
}
